import { useState, useEffect, useRef, useCallback } from "react";

const useAISummary = (allShownMessages, metadata, currentVideoTime = 0) => {
  console.log('useAISummary called with currentVideoTime:', currentVideoTime);

  const [overlayText, setOverlayText] = useState("");
  const [topics, setTopics] = useState([]);
  const [languages, setLanguages] = useState([]);

  const lastAIUpdateVideoTimeRef = useRef(0);
  const isUpdatingRef = useRef(false);
  const intervalRef = useRef(null);
  const actualVideoTimeRef = useRef(0);
  const lastVideoTitleRef = useRef('');

  const messagesRef = useRef(allShownMessages);
  const metadataRef = useRef(metadata);

  useEffect(() => {
    messagesRef.current = allShownMessages;
    metadataRef.current = metadata;
  }, [allShownMessages, metadata]);


  const VIDEO_TIME_UPDATE_INTERVAL = 30;
  const PROGRESS_CHECK_INTERVAL = 1000;
  const SEEK_THRESHOLD = 10;

  const parseLabelEmojiPairs = (input) => {
    if (!input) return [];
    const regex =
      /"([^"]+)"\s+([\p{Emoji_Presentation}\p{Extended_Pictographic}]{1,2})/gu;
    const matches = [...input.matchAll(regex)];
    return matches.map((match) => ({
      label: match[1],
      emoji: match[2],
    }));
  };


  const getCurrentVideoTime = useCallback(() => {
    const videoElement = document.querySelector('video');
    return videoElement?.currentTime || 0;
  }, []);

  const updateAISummary = useCallback(
    async (reason = "30s-interval") => {
      if (isUpdatingRef.current) {
        console.log("AI update already in progress - skipping");
        return;
      }
      
      const currentMessages = messagesRef.current;
      const currentMetadata = metadataRef.current;

      if (!currentMetadata || !currentMessages || currentMessages.length < 1) {
        console.log("Insufficient data for AI update, skipping.");
        return;
      }

      const realCurrentTime = getCurrentVideoTime();
      isUpdatingRef.current = true;

      try {
        console.log(`🚀 AI Summary Update (${reason}) at video time: ${realCurrentTime.toFixed(1)}s`);
        console.log(`Processing ${currentMessages.length} messages`);

        const requestBody = {
          source: "archive",
          chat_messages: currentMessages.slice(-50),
          videoTitle: currentMetadata.title || "",
          videoDescription: currentMetadata.description || "",
          currentTime: realCurrentTime,
          updateReason: reason,
        };

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_API_URL}/getVideoAnalysis`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify( requestBody ),
          },
        );

        console.log(`AI API Response: ${response.status} ${response.statusText}`);

        if (response.status === 429) {
          console.warn("Rate limited by server");
          setOverlayText("Rate limited - please wait...");
          return;
        }

        if (!response.ok) {
          throw new Error(`API call failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        console.log("AI summary updated successfully", {
          hasSummary: !!data.summary,
          topicsCount: data.topics ? parseLabelEmojiPairs(data.topics).length : 0,
          languagesCount: data.languages ? parseLabelEmojiPairs(data.languages).length : 0,
        });

        if (data.summary) {
          setOverlayText(data.summary);
        }
        if (data.topics) {
          setTopics(parseLabelEmojiPairs(data.topics));
        }
        if (data.languages) {
          setLanguages(parseLabelEmojiPairs(data.languages));
        }

        lastAIUpdateVideoTimeRef.current = realCurrentTime;
        console.log(`✅ lastAIUpdateVideoTimeRef updated to: ${realCurrentTime.toFixed(1)}s`);

      } catch (err) {
        console.error("Error updating AI summary:", err);
        setOverlayText(`Error: ${err.message}`);
        setTopics([]);
        setLanguages([]);
      } finally {
        isUpdatingRef.current = false;
      }
    },
    [getCurrentVideoTime] 
  );

  // ==================== INTERVAL MONITORING LOGIC ====================
  useEffect(() => {
    console.log('Setting up a stable interval');

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(() => {
      const currentMetadata = metadataRef.current; 
      
      if (!currentMetadata || !messagesRef.current || messagesRef.current.length < 1) {
        return;
      }

      const realCurrentTime = getCurrentVideoTime();
      const previousVideoTime = actualVideoTimeRef.current;
      actualVideoTimeRef.current = realCurrentTime;
      
      if (lastVideoTitleRef.current !== currentMetadata.title) {
        console.log(`📹 Video changed: "${lastVideoTitleRef.current}" -> "${currentMetadata.title}"`);
        lastVideoTitleRef.current = currentMetadata.title;
        lastAIUpdateVideoTimeRef.current = 0;
        updateAISummary("video-changed");
        return;
      }

      if (lastAIUpdateVideoTimeRef.current === 0 && realCurrentTime > 1) {
        console.log("🎬 Initial AI update for video start");
        updateAISummary("initial-load");
        return;
      }

      const timeDiffFromPrevious = Math.abs(realCurrentTime - previousVideoTime);
      if (timeDiffFromPrevious > SEEK_THRESHOLD && previousVideoTime > 0) {
        console.log(`⏩ Large seek detected: ${timeDiffFromPrevious.toFixed(1)}s jump`);
        updateAISummary("seek-detected");
        return;
      }

      const videoTimeSinceLastUpdate = realCurrentTime - lastAIUpdateVideoTimeRef.current;

      if (Math.floor(realCurrentTime) % 5 === 0 && Math.floor(realCurrentTime) !== Math.floor(previousVideoTime)) {
        console.log(`⏱️ Interval check: current=${realCurrentTime.toFixed(1)}s, last=${lastAIUpdateVideoTimeRef.current.toFixed(1)}s, diff=${videoTimeSinceLastUpdate.toFixed(1)}s`);
      }

      if (videoTimeSinceLastUpdate >= VIDEO_TIME_UPDATE_INTERVAL && !isUpdatingRef.current) {
        console.log(`⏰ 30s interval reached! Triggering AI update. Time since last: ${videoTimeSinceLastUpdate.toFixed(1)}s`);
        updateAISummary("30s-video-time");
        return;
      }

      if (videoTimeSinceLastUpdate < -SEEK_THRESHOLD && !isUpdatingRef.current) {
        console.log(`⏪ Backward seek detected.`);
        updateAISummary("backward-seek");
        return;
      }
      
      if (Math.abs(realCurrentTime - previousVideoTime) < 0.1 && videoTimeSinceLastUpdate >= VIDEO_TIME_UPDATE_INTERVAL && !isUpdatingRef.current) {
        console.log(`⏸️ Video paused but 30s passed. Triggering AI update.`);
        updateAISummary("paused-30s");
      }
    }, PROGRESS_CHECK_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [updateAISummary, getCurrentVideoTime]);

  /*
  useEffect(() => {
    if (metadata?.title && lastVideoTitleRef.current !== metadata.title) {
      console.log(`🔄 Video metadata changed to: ${metadata.title}`);
      // Логіка з цього ефекту вже є в головному інтервалі.
    }
  }, [metadata?.title, updateAISummary, getCurrentVideoTime]);
  */

  return {
    overlayText,
    topics,
    languages,
  };
};

export default useAISummary;